import { useState, useCallback, useMemo, useRef } from 'react';
import ConfirmDialog from '../components/ConfirmDialog';

interface UseConfirmOptions {
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'danger' | 'warning' | 'info' | 'success';
}

interface UseConfirmReturn {
  ConfirmComponent: React.FC;
  confirm: () => Promise<boolean>;
}

export const useConfirm = (options: UseConfirmOptions): UseConfirmReturn => {
  const [isOpen, setIsOpen] = useState(false);
  const [resolvePromise, setResolvePromise] = useState<((value: boolean) => void) | null>(null);

  const confirm = useCallback((): Promise<boolean> => {
    setIsOpen(true);
    return new Promise<boolean>((resolve) => {
      setResolvePromise(() => resolve);
    });
  }, []);

  const handleConfirm = useCallback(() => {
    if (resolvePromise) {
      resolvePromise(true);
      setResolvePromise(null);
    }
    setIsOpen(false);
  }, [resolvePromise]);

  const handleCancel = useCallback(() => {
    if (resolvePromise) {
      resolvePromise(false);
      setResolvePromise(null);
    }
    setIsOpen(false);
  }, [resolvePromise]);

  // Keep the latest state/handlers/options in a ref so ConfirmComponent can be a
  // STABLE component type (audit #230). Previously it was a brand-new function
  // every render, so React unmounted + remounted ConfirmDialog on any parent
  // re-render, resetting its focus and open/close animation. The stable memoized
  // component reads fresh values through the ref each time it renders.
  const latest = useRef({ isOpen, handleConfirm, handleCancel, options });
  latest.current = { isOpen, handleConfirm, handleCancel, options };

  const ConfirmComponent = useMemo<React.FC>(() => {
    const Component: React.FC = () => {
      const { isOpen, handleConfirm, handleCancel, options } = latest.current;
      return (
        <ConfirmDialog
          isOpen={isOpen}
          onClose={handleCancel}
          onConfirm={handleConfirm}
          title={options.title}
          message={options.message}
          confirmText={options.confirmText}
          cancelText={options.cancelText}
          variant={options.variant}
        />
      );
    };
    return Component;
  }, []);

  return { ConfirmComponent, confirm };
};
